import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, HeartPulse, Download, Crown, Sparkles, FileText, Check, Copy } from "lucide-react";

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text || ""); toast.success("Copied — paste it straight into TRAC"); }
  catch { toast.error("Couldn't copy. Please select and copy manually."); }
};

const PHASE_LABEL = {
  queued: "Starting…",
  tailoring: "Tailoring your CV to this role…",
  writing: "Writing your ~1,400-word supporting statement…",
};

export function NhsMatchSupportingDialog({ match, isPro }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [phase, setPhase] = useState("queued");
  const [result, setResult] = useState(null);

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    setPhase("queued");
    try {
      const { data } = await api.post("/nhs/supporting-info/from-match", { match_id: match.id });
      const id = data.id;
      let finished = false;
      for (let i = 0; i < 100; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: d } = await api.get(`/nhs/supporting-info/${id}`);
        if (d.phase) setPhase(d.phase);
        if (d.status === "done") { setResult(d); toast.success("Supporting statement ready"); finished = true; break; }
        if (d.status === "error") { toast.error("Generation failed. Please try again."); finished = true; break; }
      }
      if (!finished) toast.message("Still working — check back shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generation failed");
    } finally { setGenerating(false); }
  };

  return (
    <>
      <Button
        size="sm" variant="outline"
        className="border-[#005EB8] text-[#005EB8] hover:bg-[#005EB8]/5"
        onClick={() => setOpen(true)}
        data-testid={`nhs-support-btn-${match.id}`}
      >
        <HeartPulse className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} /> Supporting info
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="nhs-match-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-left">
              <HeartPulse className="h-5 w-5 text-[#005EB8] shrink-0" /> NHS Supporting Information — {match?.title}{match?.company ? ` · ${match.company}` : ""}
            </DialogTitle>
          </DialogHeader>

          {!isPro ? (
            <div className="text-center py-6" data-testid="nhs-match-pro-gate">
              <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3"><Crown className="h-6 w-6 text-accent" /></div>
              <h3 className="font-heading text-xl font-bold">This is a Sponsored-tier feature</h3>
              <Button className="mt-4" onClick={() => navigate("/billing")} data-testid="nhs-match-upgrade-btn"><Crown className="h-4 w-4 mr-1.5" /> View plans</Button>
            </div>
          ) : result && result.status === "done" ? (
            <div className="space-y-3" data-testid="nhs-match-result">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs text-muted-foreground">Approx. {result.word_count} words · review & personalise before submitting</span>
                <div className="flex gap-2">
                  {result.tailored_cv_id && (
                    <a href={`${API}/tailored-cvs/${result.tailored_cv_id}/download?fmt=docx`} target="_blank" rel="noreferrer" data-testid="nhs-match-cv-download">
                      <Button variant="outline" size="sm"><FileText className="h-4 w-4 mr-1.5" /> Enhanced CV (Word)</Button>
                    </a>
                  )}
                  <Button variant="outline" size="sm" onClick={() => copyText(result.supporting_info)} data-testid="nhs-match-copy">
                    <Copy className="h-4 w-4 mr-1.5" /> Copy
                  </Button>
                  <a href={`${API}/nhs/supporting-info/${result.id}/download?fmt=docx`} target="_blank" rel="noreferrer" data-testid="nhs-match-download-word">
                    <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1.5" /> Word</Button>
                  </a>
                  <a href={`${API}/nhs/supporting-info/${result.id}/download?fmt=pdf`} target="_blank" rel="noreferrer" data-testid="nhs-match-download">
                    <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1.5" /> PDF</Button>
                  </a>
                  <Button variant="outline" size="sm" onClick={generate} disabled={generating} data-testid="nhs-match-regenerate">
                    {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} New
                  </Button>
                </div>
              </div>
              <div className="text-sm whitespace-pre-wrap leading-relaxed bg-zinc-50 border rounded-lg p-4" data-testid="nhs-match-text">{result.supporting_info}</div>
            </div>
          ) : generating ? (
            <div className="py-10 text-center space-y-4" data-testid="nhs-match-progress">
              <Loader2 className="h-7 w-7 mx-auto animate-spin text-[#005EB8]" />
              <p className="text-sm font-medium" data-testid="nhs-match-phase">{PHASE_LABEL[phase] || "Working…"}</p>
              <div className="max-w-xs mx-auto space-y-1.5 text-left">
                <PhaseRow label="Reading the job description & person specification" done />
                <PhaseRow label="Tailoring your CV to this role" done={phase === "writing" || phase === "done"} active={phase === "tailoring"} />
                <PhaseRow label="Writing your supporting statement" done={phase === "done"} active={phase === "writing"} />
              </div>
              <p className="text-xs text-muted-foreground">This usually takes 30–60 seconds. Keep this window open.</p>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-3">
                We'll read this role's job description &amp; person specification, first enhance your CV to align closely (90–100%) with it, then write your 1,400–1,500 word supporting statement reflecting the trust's values and the NHS Constitution. We never invent experience or registrations.
              </p>
              <p className="text-xs text-[#005EB8] bg-[#005EB8]/5 border border-[#005EB8]/20 rounded-lg p-2.5 max-w-md mx-auto mb-4" data-testid="nhs-cv-tip">
                For the strongest result, make sure your profile reflects a CV suited to this role. The closer your real experience matches the role, the stronger your statement.
              </p>
              <Button onClick={generate} className="active:scale-[0.98] transition-transform" data-testid="nhs-match-generate-btn">
                <Sparkles className="h-4 w-4 mr-1.5" /> Generate supporting information
              </Button>
              <p className="text-xs text-muted-foreground mt-2">5 generations / day</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function PhaseRow({ label, done, active }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {done ? <Check className="h-4 w-4 text-emerald-600 shrink-0" strokeWidth={2.5} />
        : active ? <Loader2 className="h-4 w-4 animate-spin text-[#005EB8] shrink-0" />
        : <span className="h-4 w-4 rounded-full border border-zinc-300 shrink-0" />}
      <span className={done ? "text-foreground" : active ? "text-[#005EB8] font-medium" : "text-muted-foreground"}>{label}</span>
    </div>
  );
}
